// Universe Generator - creates plausible MSP ecosystems with realistic data
// This generates a consistent "universe" of MSPs, organizations, devices, and their histories

import type {
  Device,
  Organization,
  MSP,
  NetworkContext,
  LocationInference,
  ConnectionState,
  LocationType,
  NetworkMeasurement,
  DeviceActivity,
} from './types';

import type { DeviceLogEntry } from './client';

// =============================================================================
// Seeded Random Number Generator (for reproducible universes)
// =============================================================================

class SeededRandom {
  private seed: number;

  constructor(seed: number | string) {
    this.seed = typeof seed === 'string'
      ? seed.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
      : seed;
  }

  // Returns a number between 0 and 1
  next(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  // Returns an integer between min and max (inclusive)
  int(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  // Pick a random item from an array
  pick<T>(arr: T[]): T {
    return arr[this.int(0, arr.length - 1)];
  }

  // Pick multiple unique items from an array
  pickN<T>(arr: T[], n: number): T[] {
    const shuffled = [...arr].sort(() => this.next() - 0.5);
    return shuffled.slice(0, n);
  }

  // Returns true with given probability (0-1)
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  // Generate a weighted random choice
  weighted<T>(options: { value: T; weight: number }[]): T {
    const totalWeight = options.reduce((sum, o) => sum + o.weight, 0);
    let random = this.next() * totalWeight;
    for (const option of options) {
      random -= option.weight;
      if (random <= 0) return option.value;
    }
    return options[options.length - 1].value;
  }
}

// =============================================================================
// Name & Data Pools
// =============================================================================

const mspNames = [
  'TechSolutions MSP', 'CloudBridge IT', 'NetPro Services', 'Managed IT Partners',
  'ProActive Tech', 'SecureNet Solutions', 'IT Works MSP', 'Digital Shield',
  'CyberGuard Services', 'NextGen IT', 'Infinity Tech', 'Prime IT Solutions',
];

const orgNamePrefixes = [
  'Acme', 'Global', 'Premier', 'United', 'Pacific', 'Atlantic', 'Mountain', 'Valley',
  'Summit', 'Horizon', 'Frontier', 'Pinnacle', 'Apex', 'Vertex', 'Zenith', 'Catalyst',
];

const orgNameSuffixes = [
  'Industries', 'Corporation', 'Holdings', 'Group', 'Partners', 'Associates',
  'Ventures', 'Solutions', 'Consulting', 'Services', 'Systems', 'Technologies',
  'Enterprises', 'International', 'Dynamics', 'Capital', 'Labs', 'Works',
];

const firstNames = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'David', 'Elizabeth', 'William', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Christopher', 'Karen', 'Charles', 'Lisa', 'Daniel', 'Nancy',
  'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley',
  'Steven', 'Kimberly', 'Paul', 'Emily', 'Andrew', 'Donna', 'Joshua', 'Michelle',
  'Wei', 'Mei', 'Raj', 'Priya', 'Mohammed', 'Fatima', 'Carlos', 'Maria', 'Yuki', 'Kenji',
];

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson',
  'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
  'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker',
  'Chen', 'Wang', 'Kim', 'Patel', 'Singh', 'Nakamura', 'Tanaka', 'Ahmed', 'Kumar',
];

type IspProfile = {
  names: string[];
  networkType: 'corporate' | 'residential' | 'mobile' | 'datacenter' | 'unknown';
  baseLatency: number;
  baseLoss: number;
  baseJitter: number;
  varianceMultiplier: number;
};

const ispProfiles: Record<string, IspProfile> = {
  corporateFiber: {
    names: ['Lumen Enterprise', 'AT&T Business Fiber', 'Comcast Business', 'Verizon Business'],
    networkType: 'corporate',
    baseLatency: 8, baseLoss: 0, baseJitter: 1,
    varianceMultiplier: 0.2,
  },
  corporateCable: {
    names: ['Spectrum Business', 'Cox Business', 'Frontier Business'],
    networkType: 'corporate',
    baseLatency: 15, baseLoss: 0.1, baseJitter: 3,
    varianceMultiplier: 0.3,
  },
  residentialFiber: {
    names: ['AT&T Fiber', 'Verizon Fios', 'Google Fiber', 'CenturyLink Fiber'],
    networkType: 'residential',
    baseLatency: 12, baseLoss: 0.1, baseJitter: 2,
    varianceMultiplier: 0.3,
  },
  residentialCable: {
    names: ['Comcast Xfinity', 'Spectrum', 'Cox Communications', 'Mediacom'],
    networkType: 'residential',
    baseLatency: 28, baseLoss: 0.5, baseJitter: 8,
    varianceMultiplier: 0.5,
  },
  residentialDSL: {
    names: ['CenturyLink', 'AT&T DSL', 'Frontier DSL', 'Windstream'],
    networkType: 'residential',
    baseLatency: 55, baseLoss: 1.5, baseJitter: 18,
    varianceMultiplier: 0.6,
  },
  mobile: {
    names: ['T-Mobile', 'Verizon Wireless', 'AT&T Mobility', 'Sprint/T-Mobile'],
    networkType: 'mobile',
    baseLatency: 45, baseLoss: 1.2, baseJitter: 20,
    varianceMultiplier: 0.7,
  },
  publicWifi: {
    names: ['Starbucks WiFi', 'WeWork Networks', 'Marriott WiFi', 'Airport WiFi', 'Hotel Guest WiFi'],
    networkType: 'corporate',
    baseLatency: 65, baseLoss: 2.0, baseJitter: 25,
    varianceMultiplier: 0.8,
  },
};

const gatewayVendors = {
  enterprise: ['Cisco', 'Juniper', 'Arista', 'Palo Alto', 'Fortinet'],
  smallBusiness: ['Ubiquiti', 'Meraki', 'SonicWall', 'WatchGuard', 'Sophos'],
  consumer: ['Netgear', 'Linksys', 'ASUS', 'TP-Link', 'Arris', 'Motorola'],
};

const officeLocations = [
  'HQ - San Francisco', 'HQ - New York', 'HQ - Chicago', 'HQ - Austin', 'HQ - Seattle',
  'Branch - Los Angeles', 'Branch - Denver', 'Branch - Boston', 'Branch - Atlanta',
  'Branch - Dallas', 'Branch - Miami', 'Branch - Portland', 'Satellite - Phoenix',
];

// =============================================================================
// Universe Configuration
// =============================================================================

export interface UniverseConfig {
  // Seed for reproducible generation (defaults to 'network-weather-demo')
  seed?: number | string;
  // Number of MSPs to generate (default: 1 for single-MSP view)
  mspCount?: number;
  // Organizations per MSP (min-max)
  orgsPerMsp?: [number, number];
  // Devices per organization (min-max) - varies by org size
  devicesPerOrg?: [number, number];
  // Ratio of devices that are currently healthy (0-1)
  healthyRatio?: number;
  // Distribution of location types
  locationDistribution?: {
    office: number;
    home: number;
    remote: number;
  };
  // Generate historical data for this many hours
  historyHours?: number;
}

const defaultConfig: Required<UniverseConfig> = {
  seed: 'network-weather-demo',
  mspCount: 1,
  orgsPerMsp: [3, 6],
  devicesPerOrg: [3, 15],
  healthyRatio: 0.7,
  locationDistribution: {
    office: 0.4,
    home: 0.45,
    remote: 0.15,
  },
  historyHours: 168, // 7 days
};

// =============================================================================
// Generator Implementation
// =============================================================================

export interface GeneratedUniverse {
  msps: MSP[];
  organizations: Organization[];
  devices: Device[];
  // Helper function to get network history for a device
  getNetworkHistory: (clientId: string, hours?: number) => NetworkMeasurement[];
  // Helper function to get activity log for a device
  getActivityLog: (clientId: string) => DeviceActivity[];
  // Helper function to get telemetry logs for a device
  getDeviceLogs: (clientId: string) => DeviceLogEntry[];
  // Current latest version in this universe
  latestVersion: string;
}

export function generateUniverse(userConfig: UniverseConfig = {}): GeneratedUniverse {
  const config = { ...defaultConfig, ...userConfig };
  const rng = new SeededRandom(config.seed);

  const msps: MSP[] = [];
  const organizations: Organization[] = [];
  const devices: Device[] = [];

  // Latest version in this universe
  const latestVersion = '1.1.6.64';
  const olderVersions = ['1.1.5.58', '1.1.4.52', '1.1.3.45', '1.1.2.38', '1.1.1.32'];

  // Generate MSPs
  const mspNamePool = rng.pickN(mspNames, config.mspCount);
  for (let i = 0; i < config.mspCount; i++) {
    const mspId = `msp-${i + 1}`;
    msps.push({
      mspId,
      name: mspNamePool[i],
    });

    // Generate organizations for this MSP
    const orgCount = rng.int(config.orgsPerMsp[0], config.orgsPerMsp[1]);
    for (let j = 0; j < orgCount; j++) {
      const orgId = `org-${mspId}-${j + 1}`;
      const orgName = `${rng.pick(orgNamePrefixes)} ${rng.pick(orgNameSuffixes)}`;

      organizations.push({
        orgId,
        name: orgName,
        mspId,
      });

      // Determine org size (small, medium, large) which affects device count
      const orgSize = rng.weighted([
        { value: 'small', weight: 0.5 },
        { value: 'medium', weight: 0.35 },
        { value: 'large', weight: 0.15 },
      ]);

      let deviceCountRange: [number, number];
      switch (orgSize) {
        case 'small': deviceCountRange = [config.devicesPerOrg[0], Math.min(5, config.devicesPerOrg[1])]; break;
        case 'medium': deviceCountRange = [5, Math.min(12, config.devicesPerOrg[1])]; break;
        case 'large': deviceCountRange = [10, config.devicesPerOrg[1]]; break;
        default: deviceCountRange = config.devicesPerOrg;
      }

      const deviceCount = rng.int(deviceCountRange[0], deviceCountRange[1]);

      // Generate office locations for this org
      const orgOffices = rng.pickN(officeLocations, rng.int(1, 3));

      // Generate devices for this organization
      for (let k = 0; k < deviceCount; k++) {
        const device = generateDevice(rng, {
          mspId,
          orgId,
          orgName,
          deviceIndex: k,
          orgOffices,
          latestVersion,
          olderVersions,
          healthyRatio: config.healthyRatio,
          locationDistribution: config.locationDistribution,
        });
        devices.push(device);
      }
    }
  }

  // Create helper functions for history generation
  const getNetworkHistory = (clientId: string, hours = 24): NetworkMeasurement[] => {
    const device = devices.find(d => d.clientId === clientId);
    if (!device) return [];
    return generateNetworkHistory(new SeededRandom(clientId), device, hours);
  };

  const getActivityLog = (clientId: string): DeviceActivity[] => {
    const device = devices.find(d => d.clientId === clientId);
    if (!device) return [];
    return generateActivityLog(new SeededRandom(clientId + '-activity'), device, latestVersion);
  };

  const getDeviceLogs = (clientId: string): DeviceLogEntry[] => {
    const device = devices.find(d => d.clientId === clientId);
    if (!device) return [];
    return generateDeviceLogs(new SeededRandom(clientId + '-logs'), device);
  };

  return {
    msps,
    organizations,
    devices,
    getNetworkHistory,
    getActivityLog,
    getDeviceLogs,
    latestVersion,
  };
}

// =============================================================================
// Device Generation
// =============================================================================

interface DeviceGenerationContext {
  mspId: string;
  orgId: string;
  orgName: string;
  deviceIndex: number;
  orgOffices: string[];
  latestVersion: string;
  olderVersions: string[];
  healthyRatio: number;
  locationDistribution: { office: number; home: number; remote: number };
}

function generateDevice(rng: SeededRandom, ctx: DeviceGenerationContext): Device {
  // Generate user identity
  const firstName = rng.pick(firstNames);
  const lastName = rng.pick(lastNames);
  const userName = `${firstName} ${lastName}`;
  const emailDomain = ctx.orgName.toLowerCase().replace(/[^a-z]/g, '').slice(0, 12) + '.com';
  const userEmail = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${emailDomain}`;

  // Choose OS and version
  const os = rng.weighted<Device['os']>([
    { value: 'macOS', weight: 0.55 },
    { value: 'Windows', weight: 0.40 },
    { value: 'Linux', weight: 0.05 },
  ]);

  const osVersion = generateOsVersion(rng, os);
  const arch = os === 'macOS'
    ? rng.weighted<Device['arch']>([{ value: 'arm64', weight: 0.7 }, { value: 'x64', weight: 0.3 }])
    : 'x64';

  // Determine location type
  const locationType = rng.weighted<LocationType>([
    { value: 'office', weight: ctx.locationDistribution.office },
    { value: 'home', weight: ctx.locationDistribution.home },
    { value: 'remote', weight: ctx.locationDistribution.remote },
  ]);

  // Determine if device should be healthy
  const shouldBeHealthy = rng.chance(ctx.healthyRatio);

  // Generate connection state and timing
  let connectionState: ConnectionState;
  let hoursOffline: number;

  if (shouldBeHealthy) {
    connectionState = rng.weighted([
      { value: 'streaming' as const, weight: 0.7 },
      { value: 'idle' as const, weight: 0.3 },
    ]);
    hoursOffline = connectionState === 'streaming'
      ? rng.next() * 0.5  // Streaming: 0-30 min ago
      : rng.next() * 4;    // Idle: 0-4 hours ago
  } else {
    // Unhealthy device - either offline or very stale
    if (rng.chance(0.6)) {
      connectionState = 'offline';
      hoursOffline = rng.int(24, 168); // 1-7 days offline
    } else {
      connectionState = 'idle';
      hoursOffline = rng.int(12, 24); // 12-24 hours ago
    }
  }

  // Generate network context based on location
  const { network, location } = generateNetworkContext(rng, locationType, ctx.orgOffices, connectionState);

  // Determine version
  let currentVersion: string;
  if (shouldBeHealthy && rng.chance(0.85)) {
    currentVersion = ctx.latestVersion;
  } else {
    currentVersion = rng.pick(ctx.olderVersions);
  }

  // Generate timestamps
  const now = new Date();
  const lastCheckIn = new Date(now.getTime() - Math.max(hoursOffline * 3600000, 6 * 60000)).toISOString();
  const lastTelemetry = hoursOffline < 1
    ? new Date(now.getTime() - hoursOffline * 3600000).toISOString()
    : lastCheckIn;
  const lastSeen = new Date(Math.max(new Date(lastCheckIn).getTime(), new Date(lastTelemetry).getTime())).toISOString();

  // Generate client ID and asset ID
  const clientId = `dev-${ctx.orgId.replace('org-', '')}-${String(ctx.deviceIndex + 1).padStart(3, '0')}`;
  const assetId = generateAssetId(rng, ctx.orgName, os, ctx.deviceIndex);

  return {
    clientId,
    currentVersion,
    os,
    osVersion,
    arch,
    channel: rng.chance(0.05) ? 'beta' : 'stable',
    mspId: ctx.mspId,
    orgId: ctx.orgId,
    lastCheckIn,
    lastTelemetry,
    lastSeen,
    connectionState,
    location,
    network,
    checkInCount: rng.int(50, 3000),
    assetId,
    userName,
    userEmail,
  };
}

function generateOsVersion(rng: SeededRandom, os: Device['os']): string {
  switch (os) {
    case 'macOS':
      return rng.pick(['14.2.1', '14.2', '14.1', '14.0', '13.6', '13.5', '12.7']);
    case 'Windows':
      return rng.pick(['11.0.22631', '11.0.22621', '11.0.22000', '10.0.19045', '10.0.19044', '10.0.19043']);
    case 'Linux':
      return rng.pick(['6.5.0-Ubuntu', '5.15.0-Ubuntu', '6.1.0-Debian']);
    default:
      return '1.0';
  }
}

function generateAssetId(_rng: SeededRandom, orgName: string, os: Device['os'], index: number): string {
  const prefix = orgName.replace(/[^A-Z]/gi, '').slice(0, 4).toUpperCase() || 'DEV';
  const osCode = os === 'macOS' ? 'MAC' : os === 'Windows' ? 'PC' : 'LNX';
  return `${prefix}-${osCode}-${String(index + 1).padStart(3, '0')}`;
}

function generateNetworkContext(
  rng: SeededRandom,
  locationType: LocationType,
  orgOffices: string[],
  connectionState: ConnectionState
): { network: NetworkContext; location: LocationInference } {

  if (connectionState === 'offline') {
    return {
      network: {
        publicIp: '0.0.0.0',
        ispName: 'Unknown',
        asn: 0,
        networkType: 'unknown',
        radiusAuthenticated: false,
      },
      location: {
        type: 'unknown',
        confidence: 0,
        signals: [],
      },
    };
  }

  let ispProfile: IspProfile;
  let gatewayVendor: string;
  let radiusAuthenticated = false;
  let ssid: string | undefined;
  let signals: string[] = [];
  let confidence: number;
  let officeName: string | undefined;

  switch (locationType) {
    case 'office':
      ispProfile = rng.chance(0.7) ? ispProfiles.corporateFiber : ispProfiles.corporateCable;
      gatewayVendor = rng.pick(gatewayVendors.enterprise);
      radiusAuthenticated = rng.chance(0.85);
      ssid = `${rng.pick(['Corp', 'Office', 'Secure', 'Enterprise'])}-${rng.int(1, 5)}G`;
      officeName = rng.pick(orgOffices);
      signals = ['Corporate ASN'];
      if (radiusAuthenticated) signals.push('RADIUS authentication');
      if (ssid) signals.push('Known corporate SSID');
      confidence = 90 + rng.int(0, 10);
      break;

    case 'home':
      ispProfile = rng.weighted([
        { value: ispProfiles.residentialFiber, weight: 0.3 },
        { value: ispProfiles.residentialCable, weight: 0.5 },
        { value: ispProfiles.residentialDSL, weight: 0.2 },
      ]);
      gatewayVendor = rng.pick(gatewayVendors.consumer);
      ssid = `${rng.pick(['Home', 'WiFi', 'Network', 'Family'])}-${rng.int(1000, 9999)}`;
      signals = ['Residential ISP ASN', 'Consumer gateway vendor'];
      confidence = 75 + rng.int(0, 15);
      break;

    case 'remote':
      ispProfile = rng.weighted([
        { value: ispProfiles.publicWifi, weight: 0.4 },
        { value: ispProfiles.mobile, weight: 0.35 },
        { value: ispProfiles.residentialCable, weight: 0.25 }, // Coffee shop with their own connection
      ]);
      gatewayVendor = rng.pick([...gatewayVendors.smallBusiness, 'Unknown']);
      if (ispProfile === ispProfiles.mobile) {
        signals = ['Mobile carrier ASN'];
      } else {
        ssid = rng.pick(['Guest', 'Public', 'Free_WiFi', 'Hotspot']) + '_' + rng.int(1, 99);
        signals = ['Public/guest network pattern'];
      }
      confidence = 55 + rng.int(0, 20);
      break;

    default:
      ispProfile = ispProfiles.residentialCable;
      gatewayVendor = 'Unknown';
      confidence = 30;
  }

  // Generate a masked public IP
  const publicIp = `${rng.int(1, 223)}.${rng.int(0, 255)}.xxx.xxx`;

  return {
    network: {
      publicIp,
      ispName: rng.pick(ispProfile.names),
      asn: rng.int(1000, 65000),
      networkType: ispProfile.networkType,
      radiusAuthenticated,
      ssid,
      gatewayVendor,
    },
    location: {
      type: locationType,
      confidence,
      signals,
      officeName,
    },
  };
}

// =============================================================================
// History Generation
// =============================================================================

function generateNetworkHistory(
  rng: SeededRandom,
  device: Device,
  hours: number
): NetworkMeasurement[] {
  const history: NetworkMeasurement[] = [];
  const now = new Date();

  // Determine base network quality from device's network context
  const { baseLatency, baseLoss, baseJitter, varianceMultiplier } = getNetworkQualityProfile(device);

  // Generate hourly measurements
  for (let h = 0; h < hours; h++) {
    const timestamp = new Date(now.getTime() - h * 3600000);
    const hourOfDay = timestamp.getHours();

    // Time-based factors
    const peakHourFactor = (hourOfDay >= 9 && hourOfDay <= 17) ? 1.2 :
                           (hourOfDay >= 19 && hourOfDay <= 22) ? 1.3 : 1.0;

    // Random variation
    const variance = 1 + (rng.next() - 0.5) * varianceMultiplier * 2;

    // Occasional spikes/outages
    const hasSpike = rng.chance(0.05);
    const spikeFactor = hasSpike ? 2 + rng.next() * 3 : 1;

    history.push({
      timestamp: timestamp.toISOString(),
      latencyMs: Math.round(baseLatency * peakHourFactor * variance * spikeFactor),
      lossPercent: Math.round(Math.max(0, baseLoss * peakHourFactor * variance * spikeFactor) * 10) / 10,
      jitterMs: Math.round(baseJitter * peakHourFactor * variance * spikeFactor),
      target: 'gateway',
    });
  }

  return history.reverse(); // Oldest first
}

function getNetworkQualityProfile(device: Device): {
  baseLatency: number;
  baseLoss: number;
  baseJitter: number;
  varianceMultiplier: number;
} {
  // Handle devices without network context
  if (!device.network) {
    return { baseLatency: 40, baseLoss: 1, baseJitter: 15, varianceMultiplier: 0.5 };
  }

  const networkType = device.network.networkType;
  const ispName = device.network.ispName;

  // Match ISP profile
  for (const profile of Object.values(ispProfiles)) {
    if (profile.names.some(name => ispName.includes(name.split(' ')[0]))) {
      return profile;
    }
  }

  // Default based on network type
  switch (networkType) {
    case 'corporate':
      return device.network.radiusAuthenticated
        ? ispProfiles.corporateFiber
        : ispProfiles.publicWifi;
    case 'residential':
      return ispProfiles.residentialCable;
    case 'mobile':
      return ispProfiles.mobile;
    default:
      return { baseLatency: 40, baseLoss: 1, baseJitter: 15, varianceMultiplier: 0.5 };
  }
}

function generateActivityLog(
  rng: SeededRandom,
  device: Device,
  latestVersion: string
): DeviceActivity[] {
  const activities: DeviceActivity[] = [];
  const now = new Date();

  // Recent check-ins
  const checkInInterval = 4; // hours
  for (let i = 0; i < 24; i += checkInInterval) {
    if (rng.chance(0.9)) { // 90% of check-ins succeed
      activities.push({
        timestamp: new Date(now.getTime() - i * 3600000).toISOString(),
        event: 'check-in',
        detail: 'Routine heartbeat',
      });
    }
  }

  // Version update (if not on latest)
  if (device.currentVersion !== latestVersion) {
    activities.push({
      timestamp: new Date(now.getTime() - rng.int(2, 14) * 24 * 3600000).toISOString(),
      event: 'update',
      detail: `Updated to v${device.currentVersion}`,
    });
  } else {
    activities.push({
      timestamp: new Date(now.getTime() - rng.int(1, 7) * 24 * 3600000).toISOString(),
      event: 'update',
      detail: `Updated to v${latestVersion}`,
    });
  }

  // Installation event
  activities.push({
    timestamp: new Date(now.getTime() - rng.int(7, 90) * 24 * 3600000).toISOString(),
    event: 'install',
    detail: 'Initial installation',
  });

  // Occasional errors for unhealthy devices
  if (device.connectionState === 'offline' || device.connectionState === 'idle') {
    if (rng.chance(0.3)) {
      activities.push({
        timestamp: new Date(now.getTime() - rng.int(1, 48) * 3600000).toISOString(),
        event: 'error',
        detail: rng.pick([
          'Connection timeout to ingest server',
          'DNS resolution failed',
          'TLS handshake error',
          'Network unreachable',
        ]),
      });
    }
  }

  // Sort by timestamp descending (most recent first)
  return activities.sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

// =============================================================================
// Telemetry Log Generation
// =============================================================================

const LOG_ENTRY_COUNT_MIN = 20;
const LOG_ENTRY_COUNT_MAX = 80;
const LOG_HISTORY_HOURS = 48;

type LogSeverity = 'DEBUG' | 'INFO' | 'NOTICE' | 'WARNING' | 'ERROR' | 'CRITICAL';

const healthySeverityWeights: { value: LogSeverity; weight: number }[] = [
  { value: 'INFO', weight: 0.50 },
  { value: 'DEBUG', weight: 0.20 },
  { value: 'WARNING', weight: 0.15 },
  { value: 'NOTICE', weight: 0.05 },
  { value: 'ERROR', weight: 0.08 },
  { value: 'CRITICAL', weight: 0.02 },
];

const unhealthySeverityWeights: { value: LogSeverity; weight: number }[] = [
  { value: 'INFO', weight: 0.25 },
  { value: 'DEBUG', weight: 0.10 },
  { value: 'WARNING', weight: 0.25 },
  { value: 'NOTICE', weight: 0.05 },
  { value: 'ERROR', weight: 0.25 },
  { value: 'CRITICAL', weight: 0.10 },
];

function generateLogPayload(rng: SeededRandom, severity: LogSeverity, device: Device): Record<string, unknown> {
  const infoPayloads = [
    () => ({ event: 'latency_probe', target: 'gateway', latencyMs: rng.int(5, 120), lossPercent: rng.int(0, 3) / 10 }),
    () => ({ event: 'dns_lookup', domain: rng.pick(['api.networkweather.com', 'logs.networkweather.com', 'google.com', 'cloudflare.com']), resolvedIn: `${rng.int(1, 80)}ms`, server: `${rng.int(1, 223)}.${rng.int(0, 255)}.${rng.int(0, 255)}.${rng.int(0, 255)}` }),
    () => ({ event: 'connection_state', from: rng.pick(['idle', 'streaming']), to: rng.pick(['streaming', 'idle']), interface: device.network?.ssid ? 'wifi' : 'ethernet' }),
    () => ({ event: 'check_in', status: 'ok', nextIn: '4h', serverVersion: '2.1.0' }),
    () => ({ event: 'version_check', current: device.currentVersion, latest: '1.1.6.64', updateAvailable: device.currentVersion !== '1.1.6.64' }),
    () => ({ event: 'telemetry_upload', records: rng.int(10, 200), bytesCompressed: rng.int(1024, 32768), durationMs: rng.int(50, 800) }),
  ];

  const debugPayloads = [
    () => ({ event: 'arp_scan', entriesFound: rng.int(3, 40), interface: rng.pick(['en0', 'en1', 'bridge0']), durationMs: rng.int(100, 2000) }),
    () => ({ event: 'gateway_discovery', vendor: device.network?.gatewayVendor || 'Unknown', method: rng.pick(['upnp', 'mdns', 'http_fingerprint', 'arp']), ip: `192.168.${rng.int(0, 10)}.1` }),
    () => ({ event: 'wifi_scan', networksFound: rng.int(2, 25), currentBssid: `${rng.int(10, 99)}:${rng.int(10, 99)}:${rng.int(10, 99)}:xx:xx:xx`, signalDbm: -rng.int(30, 85) }),
  ];

  const warningPayloads = [
    () => ({ event: 'latency_spike', target: 'gateway', latencyMs: rng.int(150, 500), baseline: rng.int(10, 40), factor: `${(rng.int(3, 12))}x` }),
    () => ({ event: 'packet_loss', target: rng.pick(['gateway', 'dns', 'internet']), lossPercent: rng.int(2, 15), window: '5m' }),
    () => ({ event: 'wifi_roaming', fromBssid: `${rng.int(10, 99)}:xx:xx:xx`, toBssid: `${rng.int(10, 99)}:xx:xx:xx`, reason: rng.pick(['signal_degraded', 'load_balance', 'ap_disconnected']) }),
    () => ({ event: 'dns_slow', domain: rng.pick(['api.networkweather.com', 'login.microsoftonline.com']), resolvedIn: `${rng.int(500, 3000)}ms`, retries: rng.int(1, 3) }),
  ];

  const errorPayloads = [
    () => ({ event: 'connection_failed', target: 'logs.networkweather.com', error: rng.pick(['ETIMEDOUT', 'ECONNREFUSED', 'ECONNRESET', 'EHOSTUNREACH']), retryIn: `${rng.int(30, 120)}s` }),
    () => ({ event: 'dns_failure', domain: rng.pick(['api.networkweather.com', 'logs.networkweather.com']), error: 'NXDOMAIN', server: `${rng.int(1, 223)}.${rng.int(0, 255)}.0.1` }),
    () => ({ event: 'tls_error', host: 'api.networkweather.com', error: rng.pick(['certificate_expired', 'handshake_timeout', 'protocol_version_mismatch']) }),
    () => ({ event: 'upload_failed', records: rng.int(50, 300), error: rng.pick(['timeout', 'server_503', 'connection_reset']), attempt: rng.int(1, 3) }),
  ];

  const criticalPayloads = [
    () => ({ event: 'network_unreachable', interface: rng.pick(['en0', 'en1']), gateway: 'unreachable', durationSec: rng.int(60, 600) }),
    () => ({ event: 'service_crash', component: rng.pick(['telemetry_collector', 'network_monitor', 'dns_resolver']), exitCode: rng.int(1, 139), restartCount: rng.int(1, 5) }),
  ];

  switch (severity) {
    case 'DEBUG': return rng.pick(debugPayloads)();
    case 'INFO': return rng.pick(infoPayloads)();
    case 'NOTICE': return rng.pick(infoPayloads)();
    case 'WARNING': return rng.pick(warningPayloads)();
    case 'ERROR': return rng.pick(errorPayloads)();
    case 'CRITICAL': return rng.pick(criticalPayloads)();
  }
}

function generateDeviceLogs(rng: SeededRandom, device: Device): DeviceLogEntry[] {
  const isUnhealthy = device.connectionState === 'offline' || device.connectionState === 'idle';
  const weights = isUnhealthy ? unhealthySeverityWeights : healthySeverityWeights;
  const entryCount = rng.int(LOG_ENTRY_COUNT_MIN, LOG_ENTRY_COUNT_MAX);
  const now = new Date();

  const logs: DeviceLogEntry[] = [];
  for (let i = 0; i < entryCount; i++) {
    const hoursAgo = rng.next() * LOG_HISTORY_HOURS;
    const timestamp = new Date(now.getTime() - hoursAgo * 3600000);
    const severity = rng.weighted(weights);

    logs.push({
      timestamp: timestamp.toISOString(),
      severity,
      payload: generateLogPayload(rng, severity, device),
    });
  }

  // Sort newest first
  return logs.sort((a, b) =>
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

// =============================================================================
// Export default universe for the demo
// =============================================================================

let cachedUniverse: GeneratedUniverse | null = null;

export function getDefaultUniverse(): GeneratedUniverse {
  if (!cachedUniverse) {
    cachedUniverse = generateUniverse({
      seed: 'network-weather-msp-demo-2025',
      mspCount: 1,
      orgsPerMsp: [4, 4], // Exactly 4 orgs for the demo
      devicesPerOrg: [3, 12],
      healthyRatio: 0.72,
      locationDistribution: {
        office: 0.35,
        home: 0.48,
        remote: 0.17,
      },
    });
  }
  return cachedUniverse;
}

// Allow regeneration with different config
export function regenerateUniverse(config: UniverseConfig): GeneratedUniverse {
  cachedUniverse = generateUniverse(config);
  return cachedUniverse;
}
